import { useState, useRef, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Pause, X, Volume2, VolumeX, ChevronLeft, ChevronRight } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface FeaturedVideo {
  id: string;
  title: string;
  description: string | null;
  video_url: string | null;
  thumbnail_url: string | null;
  display_order: number;
}

// Utility functions for YouTube URL handling
const getYouTubeVideoId = (url: string): string | null => {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s?]+)/,
    /youtube\.com\/shorts\/([^&\s?]+)/,
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) return match[1];
  }
  return null;
};

const isYouTubeUrl = (url: string | null): boolean => {
  if (!url) return false;
  return url.includes('youtube.com') || url.includes('youtu.be');
};

const getYouTubeThumbnail = (videoId: string): string => {
  // Use sddefault (640x480) instead of maxresdefault (1280x720) for better performance
  return `https://img.youtube.com/vi/${videoId}/sddefault.jpg`;
};

interface FullscreenModalProps {
  videos: FeaturedVideo[];
  currentIndex: number;
  onClose: () => void;
  onNavigate: (index: number) => void;
}

const FullscreenVideoModal = ({ videos, currentIndex, onClose, onNavigate }: FullscreenModalProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(true);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [hasPlaybackError, setHasPlaybackError] = useState(false);

  const video = videos[currentIndex];
  const hasNext = currentIndex < videos.length - 1;
  const hasPrev = currentIndex > 0;
  const youtubeId = video.video_url ? getYouTubeVideoId(video.video_url) : null;
  const isYouTube = isYouTubeUrl(video.video_url);

  const minSwipeDistance = 50;


  const goToNext = useCallback(() => {
    if (hasNext) onNavigate(currentIndex + 1);
  }, [hasNext, currentIndex, onNavigate]);

  const goToPrev = useCallback(() => {
    if (hasPrev) onNavigate(currentIndex - 1);
  }, [hasPrev, currentIndex, onNavigate]);

  useEffect(() => {
    setHasPlaybackError(false);
    if (videoRef.current && video.video_url && !isYouTube) {
      videoRef.current.load();
      videoRef.current.play().catch(() => {
        // autoplay can be blocked on mobile; keep UI usable
      });
      setIsPlaying(true);
      setProgress(0);
      setCurrentTime(0);
    }
  }, [currentIndex, video.video_url, isYouTube]);

  useEffect(() => {
    if (isYouTube) return; // Skip for YouTube videos
    const videoElement = videoRef.current;
    if (!videoElement) return;

    const handleTimeUpdate = () => {
      if (videoElement.duration) {
        setCurrentTime(videoElement.currentTime);
        setProgress((videoElement.currentTime / videoElement.duration) * 100);
      }
    };

    const handleLoadedMetadata = () => {
      setDuration(videoElement.duration);
    };

    videoElement.addEventListener('timeupdate', handleTimeUpdate);
    videoElement.addEventListener('loadedmetadata', handleLoadedMetadata);

    return () => {
      videoElement.removeEventListener('timeupdate', handleTimeUpdate);
      videoElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
    };
  }, [currentIndex]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') goToPrev();
      if (e.key === 'ArrowRight') goToNext();
    };
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [onClose, goToNext, goToPrev]);

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    if (distance > minSwipeDistance) goToNext();
    else if (distance < -minSwipeDistance) goToPrev();
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (!progressRef.current || !videoRef.current) return;
    const rect = progressRef.current.getBoundingClientRect();
    const clickPosition = (e.clientX - rect.left) / rect.width;
    const newTime = clickPosition * duration;
    videoRef.current.currentTime = newTime;
    setCurrentTime(newTime);
    setProgress(clickPosition * 100);
  };

  const toggleMute = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const togglePlay = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (videoRef.current) {
      if (isPlaying) videoRef.current.pause();
      else videoRef.current.play();
      setIsPlaying(!isPlaying);
    }
  };

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/95 backdrop-blur-sm"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <button
        onClick={onClose}
        className="absolute top-6 right-6 z-50 w-12 h-12 rounded-full bg-muted/80 hover:bg-muted flex items-center justify-center transition-colors"
        aria-label="Close video"
      >
        <X className="h-6 w-6 text-foreground" />
      </button>

      {hasPrev && (
        <button
          onClick={(e) => { e.stopPropagation(); goToPrev(); }}
          className="absolute left-4 md:left-8 z-50 w-12 h-12 rounded-full bg-muted/80 hover:bg-muted items-center justify-center transition-colors hidden md:flex"
          aria-label="Previous video"
        >
          <ChevronLeft className="h-6 w-6 text-foreground" />
        </button>
      )}
      {hasNext && (
        <button
          onClick={(e) => { e.stopPropagation(); goToNext(); }}
          className="absolute right-4 md:right-8 z-50 w-12 h-12 rounded-full bg-muted/80 hover:bg-muted items-center justify-center transition-colors hidden md:flex"
          aria-label="Next video"
        >
          <ChevronRight className="h-6 w-6 text-foreground" />
        </button>
      )}

      <div className="absolute top-6 left-6 z-50 px-3 py-1.5 rounded-full bg-muted/80 text-sm text-foreground">
        {currentIndex + 1} / {videos.length}
      </div>

      <motion.div
        key={currentIndex}
        className="relative w-full max-w-5xl mx-4 aspect-video rounded-2xl overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.2 }}
      >
        {isYouTube && youtubeId ? (
          <iframe
            src={`https://www.youtube.com/embed/${youtubeId}?autoplay=1&rel=0&modestbranding=1`}
            className="absolute inset-0 w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            title={video.title}
          />
        ) : (
          <>
            {/* Always render a visible background so mobile never looks "blank" */}
            {video.thumbnail_url ? (
              <img
                src={video.thumbnail_url}
                alt={video.title}
                className="absolute inset-0 w-full h-full object-cover"
              />
            ) : (
              <div className="absolute inset-0 bg-gradient-to-br from-muted to-muted-foreground/20" />
            )}

            {!hasPlaybackError && (
              <video
                ref={videoRef}
                src={video.video_url || ''}
                className="absolute inset-0 w-full h-full object-cover"
                loop
                playsInline
                autoPlay
                poster={video.thumbnail_url || undefined}
                onError={() => setHasPlaybackError(true)}
              />
            )}
          </>
        )}

        <div className={`absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-background/90 via-background/50 to-transparent ${isYouTube ? 'pointer-events-none' : ''}`}>
          <p className="text-lg font-medium text-foreground mb-1">{video.title}</p>
          {video.description && (
            <p className="text-sm text-muted-foreground mb-3">{video.description}</p>
          )}

          {!isYouTube && (
            <>
              <div className="mb-4">
                <div
                  ref={progressRef}
                  className="relative h-1.5 bg-muted/50 rounded-full cursor-pointer group pointer-events-auto"
                  onClick={handleProgressClick}
                >
                  <div
                    className="absolute inset-y-0 left-0 bg-primary rounded-full transition-all"
                    style={{ width: `${progress}%` }}
                  />
                  <div
                    className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-primary rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-md"
                    style={{ left: `calc(${progress}% - 6px)` }}
                  />
                </div>
                <div className="flex justify-between mt-1.5 text-xs text-muted-foreground">
                  <span>{formatTime(currentTime)}</span>
                  <span>{formatTime(duration)}</span>
                </div>
              </div>

              <div className="flex items-center gap-3 pointer-events-auto">
                <button
                  onClick={togglePlay}
                  className="w-12 h-12 rounded-full bg-primary/90 hover:bg-primary flex items-center justify-center transition-colors"
                  aria-label={isPlaying ? 'Pause' : 'Play'}
                >
                  {isPlaying ? (
                    <Pause className="h-5 w-5 text-primary-foreground" />
                  ) : (
                    <Play className="h-5 w-5 text-primary-foreground ml-0.5" />
                  )}
                </button>
                <button
                  onClick={toggleMute}
                  className="w-12 h-12 rounded-full bg-muted/80 hover:bg-muted flex items-center justify-center transition-colors"
                  aria-label={isMuted ? 'Unmute' : 'Mute'}
                >
                  {isMuted ? (
                    <VolumeX className="h-5 w-5 text-foreground" />
                  ) : (
                    <Volume2 className="h-5 w-5 text-foreground" />
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

const VideoCard = ({
  video,
  index,
  onOpenFullscreen,
}: {
  video: FeaturedVideo;
  index: number;
  onOpenFullscreen: (video: FeaturedVideo) => void;
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [hasPreviewError, setHasPreviewError] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  
  const youtubeId = video.video_url ? getYouTubeVideoId(video.video_url) : null;
  const isYouTube = isYouTubeUrl(video.video_url);
  const thumbnailUrl = video.thumbnail_url || (youtubeId ? getYouTubeThumbnail(youtubeId) : null);

  // Use IntersectionObserver for mobile auto-play on scroll
  useEffect(() => {
    const element = cardRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsInView(entry.isIntersecting);
        });
      },
      { threshold: 0.5 }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  // Auto-play video when in view on mobile (no hover events)
  useEffect(() => {
    if (!videoRef.current || !video.video_url || isYouTube) return;
    
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    
    if (isTouchDevice && isInView) {
      videoRef.current.play().catch(() => {});
      setIsHovered(true);
    } else if (isTouchDevice && !isInView && isHovered) {
      videoRef.current.pause();
      setIsHovered(false);
    }
  }, [isInView, video.video_url, isYouTube, isHovered]);

  const handleMouseEnter = () => {
    if ('ontouchstart' in window) return;
    setIsHovered(true);
    if (videoRef.current && video.video_url && !isYouTube) {
      videoRef.current.play();
    }
  };

  const handleMouseLeave = () => {
    if ('ontouchstart' in window) return;
    setIsHovered(false);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  return (
    <motion.div
      ref={cardRef}
      className="relative aspect-video bg-muted rounded-xl overflow-hidden group cursor-pointer"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      onClick={() => onOpenFullscreen(video)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Always render a visible background so mobile never looks "blank" */}
      {thumbnailUrl ? (
        <img
          src={thumbnailUrl}
          alt={video.title}
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
        />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-muted to-muted-foreground/20" />
      )}

      {/* Video preview overlays the background when it can play */}
      {!isYouTube && video.video_url && !hasPreviewError && (
        <video
          ref={videoRef}
          src={video.video_url}
          className="absolute inset-0 w-full h-full object-cover"
          loop
          muted
          playsInline
          preload="metadata"
          poster={thumbnailUrl || undefined}
          onError={() => setHasPreviewError(true)}
        />
      )}

      <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-background/90 to-transparent">
        <p className="font-medium text-foreground">{video.title}</p>
        {video.description && (
          <p className="text-sm text-muted-foreground line-clamp-1 mt-1">{video.description}</p>
        )}
      </div>

      <div className="absolute inset-0 flex items-center justify-center">
        <div
          className={`w-16 h-16 rounded-full bg-primary/90 flex items-center justify-center transition-all duration-300 ${
            isHovered ? 'opacity-0 scale-90' : 'opacity-100 scale-100'
          }`}
        >
          <Play className="h-7 w-7 text-primary-foreground ml-1" />
        </div>
      </div>

      <div className="absolute inset-0 rounded-xl ring-1 ring-foreground/10" />
    </motion.div>
  );
};

export const FeaturedVideosSection = () => {
  const [fullscreenIndex, setFullscreenIndex] = useState<number | null>(null);

  const { data: videos = [], isLoading } = useQuery({
    queryKey: ['featured-videos'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('featured_videos')
        .select('id, title, description, video_url, thumbnail_url, display_order')
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (error) throw error;
      return data as FeaturedVideo[];
    },
  });

  const handleOpenFullscreen = (video: FeaturedVideo) => {
    const index = videos.findIndex((v) => v.id === video.id);
    setFullscreenIndex(index >= 0 ? index : null);
  };

  if (!isLoading && videos.length === 0) {
    return null;
  }

  return (
    <>
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-primary font-medium tracking-wide uppercase text-sm mb-4 block">
              Our Work
            </span>
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-4">
              Featured Showreels
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Explore our cinematic productions and discover the artistry behind every frame
            </p>
          </motion.div>

          {isLoading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="relative aspect-video bg-muted rounded-xl overflow-hidden">
                  {/* Shimmer background */}
                  <div className="absolute inset-0 bg-gradient-to-br from-muted via-muted-foreground/5 to-muted animate-pulse" />
                  {/* Content skeleton */}
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <div className="h-5 w-3/4 bg-muted-foreground/10 rounded mb-2 animate-pulse" />
                    <div className="h-4 w-1/2 bg-muted-foreground/10 rounded animate-pulse" />
                  </div>
                  {/* Play button skeleton */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-16 h-16 rounded-full bg-muted-foreground/10 animate-pulse" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {videos.map((video, index) => (
                <VideoCard
                  key={video.id}
                  video={video}
                  index={index}
                  onOpenFullscreen={handleOpenFullscreen}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      <AnimatePresence>
        {fullscreenIndex !== null && videos.length > 0 && (
          <FullscreenVideoModal
            videos={videos}
            currentIndex={fullscreenIndex}
            onClose={() => setFullscreenIndex(null)}
            onNavigate={setFullscreenIndex}
          />
        )}
      </AnimatePresence>
    </>
  );
};
